import React from 'react';
import { Autocomplete, Chip, TextField, createFilterOptions } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  canonicalizeInventoryComment,
  INVENTORY_COMMENT_THEMES,
  isInventoryCommentTheme,
  parseInventoryComments,
  serializeInventoryComments,
} from '../constants/inventoryCommentThemes';

const ACCENT = '#3B82F6';

const filterCommentOptions = createFilterOptions<string>({
  stringify: (option) => option,
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 0.5,
    py: 0.5,
    minHeight: 40,
    '& fieldset': { border: 'none' },
    '&.Mui-focused': { boxShadow: `0 0 0 2px ${ACCENT}` },
  },
  '& .MuiAutocomplete-input': {
    minWidth: 72,
    py: '4px',
  },
};

const listSlotProps = {
  paper: {
    elevation: 0,
    sx: {
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      py: 0.5,
      maxHeight: 280,
      '& .MuiAutocomplete-option': { borderRadius: 1, my: 0.125 },
    },
  },
  popper: {
    sx: { zIndex: 1400 },
  },
} as const;

function chipSx(isTheme: boolean) {
  return {
    height: 24,
    maxWidth: '100%',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '9999px',
    bgcolor: isTheme ? 'rgba(59, 130, 246, 0.12)' : 'rgba(148, 163, 184, 0.18)',
    color: isTheme ? 'rgb(29, 78, 216)' : 'rgb(71, 85, 105)',
    '& .MuiChip-deleteIcon': {
      fontSize: 16,
      color: 'inherit',
      opacity: 0.7,
      '&:hover': { opacity: 1, color: 'inherit' },
    },
    '& .MuiChip-label': { px: 1 },
  };
}

interface InventoryCommentsSelectProps {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
}

function InventoryCommentsSelect({
  value,
  onChange,
  disabled,
}: InventoryCommentsSelectProps): React.ReactElement {
  const selected = parseInventoryComments(value);

  return (
    <Autocomplete<string, true, true, true>
      multiple
      freeSolo
      disableClearable
      disableCloseOnSelect
      filterSelectedOptions
      disabled={disabled}
      size="small"
      fullWidth
      options={[...INVENTORY_COMMENT_THEMES]}
      value={selected}
      filterOptions={(options, params) => {
        const selectedKeys = new Set(selected.map((item) => item.toLowerCase()));
        const available = options.filter((option) => !selectedKeys.has(option.toLowerCase()));
        const filtered = filterCommentOptions(available, params);
        const input = canonicalizeInventoryComment(params.inputValue);
        if (!input) return filtered;
        const alreadyListed = [...filtered, ...selected].some(
          (option) => option.toLowerCase() === input.toLowerCase()
        );
        if (!alreadyListed) filtered.push(input);
        return filtered;
      }}
      onChange={(_, next) => {
        onChange(serializeInventoryComments(next.map((item) => String(item))));
      }}
      getOptionLabel={(option) => option}
      isOptionEqualToValue={(a, b) => a.toLowerCase() === b.toLowerCase()}
      noOptionsText="Escribe para añadir un comentario"
      slotProps={listSlotProps}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const tagProps = getTagProps({ index });
          const isTheme = isInventoryCommentTheme(tag);
          return (
            <Chip
              key={tagProps.key}
              className={tagProps.className}
              disabled={tagProps.disabled}
              tabIndex={tagProps.tabIndex}
              data-tag-index={tagProps['data-tag-index']}
              label={tag}
              size="small"
              onDelete={disabled ? undefined : tagProps.onDelete}
              sx={chipSx(isTheme)}
            />
          );
        })
      }
      renderOption={(optionProps, option) => {
        const isTheme = isInventoryCommentTheme(option);
        return React.createElement('li', optionProps, isTheme ? option : `Añadir «${option}»`);
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          id={params.id}
          disabled={params.disabled}
          fullWidth
          size={params.size}
          placeholder={selected.length === 0 ? 'Seleccionar o escribir…' : 'Añadir…'}
          sx={fieldSx}
          slotProps={{
            inputLabel: params.InputLabelProps,
            htmlInput: params.inputProps,
            input: {
              ref: params.InputProps.ref,
              className: params.InputProps.className,
              startAdornment: params.InputProps.startAdornment,
              endAdornment: params.InputProps.endAdornment,
            },
          }}
        />
      )}
    />
  );
}

export default InventoryCommentsSelect;
